import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { ThemeProvider } from "./context/ThemeContext";

// Pages
import HomePage from "./pages/HomePage";
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ProductsPage = lazy(() => import("./pages/ProductsPage"));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const ServiceDetailPage = lazy(() => import("./pages/ServiceDetailPage"));
const SolutionsPage = lazy(() => import("./pages/SolutionsPage"));
const SolutionDetailPage = lazy(() => import("./pages/SolutionDetailPage"));
const ApplicationsPage = lazy(() => import("./pages/ApplicationsPage"));
const ApplicationDetailPage = lazy(() => import("./pages/ApplicationDetailPage"));
const SectorsPage = lazy(() => import("./pages/SectorsPage"));
const SectorDetailPage = lazy(() => import("./pages/SectorDetailPage"));
const ValveMaintenancePage = lazy(() => import("./pages/ValveMaintenancePage"));
const DocumentationPage = lazy(() => import("./pages/DocumentationPage"));
const NewsPage = lazy(() => import("./pages/NewsPage"));
const NewsDetailPage = lazy(() => import("./pages/NewsDetailPage"));
const SiloTruckLoadingPage = lazy(() => import("./pages/SiloTruckLoadingPage"));
const VideosPage = lazy(() => import("./pages/VideosPage"));
const EnergyTransitionPage = lazy(() => import("./pages/EnergyTransitionPage"));
const CustomerStoriesPage = lazy(() => import("./pages/CustomerStoriesPage"));
const CustomerStoryDetailPage = lazy(() => import("./pages/CustomerStoryDetailPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const CookiePolicyPage = lazy(() => import("./pages/CookiePolicyPage"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
const ScrollToTopButton = lazy(() => import("./components/ScrollToTop"));

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <LanguageProvider>
          <div className="App min-h-screen bg-white dark:bg-gray-900 transition-colors duration-300">
          <ScrollToTop />
          <Suspense
            fallback={
              <main className="min-h-screen pt-[104px] flex items-center justify-center bg-white dark:bg-gray-900">
                <div className="h-10 w-10 rounded-full border-4 border-[#213A8F]/25 border-t-[#213A8F] animate-spin" aria-hidden="true" />
                <span className="sr-only">Loading page...</span>
              </main>
            }
          >
            <Routes>
              {/* Home */}
              <Route path="/" element={<HomePage />} />
              <Route path="/en" element={<HomePage />} />
              <Route path="/az" element={<HomePage />} />

              {/* Valve Maintenance - TR/EN/AZ */}
              <Route path="/vana-bakim" element={<ValveMaintenancePage />} />
              <Route path="/valve-maintenance" element={<ValveMaintenancePage />} />
              <Route path="/vana-baxim" element={<ValveMaintenancePage />} />

              {/* Products - TR/EN/AZ */}
              <Route path="/urunler" element={<ProductsPage />} />
              <Route path="/urunler/:slug" element={<ProductDetailPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:slug" element={<ProductDetailPage />} />
              <Route path="/mehsullar" element={<ProductsPage />} />
              <Route path="/mehsullar/:slug" element={<ProductDetailPage />} />

              {/* Services - TR/EN/AZ */}
              <Route path="/hizmetler" element={<ServicesPage />} />
              <Route path="/hizmetler/:slug" element={<ServiceDetailPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/xidmetler" element={<ServicesPage />} />
              <Route path="/xidmetler/:slug" element={<ServiceDetailPage />} />

              {/* Solutions - TR/EN/AZ */}
              <Route path="/cozumler" element={<SolutionsPage />} />
              <Route path="/cozumler/:slug" element={<SolutionDetailPage />} />
              <Route path="/solutions" element={<SolutionsPage />} />
              <Route path="/solutions/:slug" element={<SolutionDetailPage />} />
              <Route path="/heller" element={<SolutionsPage />} />
              <Route path="/heller/:slug" element={<SolutionDetailPage />} />

              {/* Applications - TR/EN/AZ */}
              <Route path="/uygulamalar" element={<ApplicationsPage />} />
              <Route path="/uygulamalar/:slug" element={<ApplicationDetailPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/applications/:slug" element={<ApplicationDetailPage />} />
              <Route path="/tetbiqler" element={<ApplicationsPage />} />
              <Route path="/tetbiqler/:slug" element={<ApplicationDetailPage />} />

              {/* Industries/Sectors - TR/EN/AZ */}
              <Route path="/sektorler" element={<SectorsPage />} />
              <Route path="/sektorler/:slug" element={<SectorDetailPage />} />
              <Route path="/industries" element={<SectorsPage />} />
              <Route path="/industries/:slug" element={<SectorDetailPage />} />
              <Route path="/senayeler" element={<SectorsPage />} />
              <Route path="/senayeler/:slug" element={<SectorDetailPage />} />

              {/* About - TR/EN/AZ */}
              <Route path="/hakkimizda" element={<AboutPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/haqqimizda" element={<AboutPage />} />

              {/* Contact - TR/EN/AZ */}
              <Route path="/iletisim" element={<ContactPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/elaqe" element={<ContactPage />} />

              {/* FAQ - TR/EN/AZ */}
              <Route path="/sss" element={<FaqPage />} />
              <Route path="/faq" element={<FaqPage />} />
              <Route path="/tss" element={<FaqPage />} />

              {/* Documentation - TR/EN/AZ */}
              <Route path="/dokumantasyon" element={<DocumentationPage />} />
              <Route path="/documentation" element={<DocumentationPage />} />
              <Route path="/dokumentasiya" element={<DocumentationPage />} />

              {/* News - TR/EN/AZ */}
              <Route path="/haberler" element={<NewsPage />} />
              <Route path="/haberler/:slug" element={<NewsDetailPage />} />
              <Route path="/news" element={<NewsPage />} />
              <Route path="/news/:slug" element={<NewsDetailPage />} />
              <Route path="/xeberler" element={<NewsPage />} />
              <Route path="/xeberler/:slug" element={<NewsDetailPage />} />

              {/* Silo Truck Loading Precision Landing - TR/EN/AZ */}
              <Route path="/cozumler/silo-ve-kamyon-yukleme-hassasiyeti" element={<SiloTruckLoadingPage />} />
              <Route path="/solutions/silo-and-truck-loading-precision" element={<SiloTruckLoadingPage />} />
              <Route path="/heller/silo-ve-kamyon-yukleme-deqiqliyi" element={<SiloTruckLoadingPage />} />

              {/* Videos - TR/EN/AZ */}
              <Route path="/videolar" element={<VideosPage />} />
              <Route path="/videos" element={<VideosPage />} />
              <Route path="/videolar-az" element={<VideosPage />} />

              {/* Energy Transition - TR/EN/AZ */}
              <Route path="/enerji-donusumu" element={<EnergyTransitionPage />} />
              <Route path="/energy-transition" element={<EnergyTransitionPage />} />
              <Route path="/enerji-kecidi" element={<EnergyTransitionPage />} />

              {/* Customer Stories - TR/EN/AZ */}
              <Route path="/musteri-hikayeleri" element={<CustomerStoriesPage />} />
              <Route path="/musteri-hikayeleri/:storyId" element={<CustomerStoryDetailPage />} />
              <Route path="/customer-stories" element={<CustomerStoriesPage />} />
              <Route path="/customer-stories/:storyId" element={<CustomerStoryDetailPage />} />
              <Route path="/musteri-hekayeleri" element={<CustomerStoriesPage />} />
              <Route path="/musteri-hekayeleri/:storyId" element={<CustomerStoryDetailPage />} />

              {/* Privacy Policy - TR/EN/AZ */}
              <Route path="/gizlilik-politikasi" element={<PrivacyPolicyPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/mexfilik-siyaseti" element={<PrivacyPolicyPage />} />

              {/* Cookie Policy - TR/EN/AZ */}
              <Route path="/cerez-politikasi" element={<CookiePolicyPage />} />
              <Route path="/cookie-policy" element={<CookiePolicyPage />} />
              <Route path="/cerez-siyaseti" element={<CookiePolicyPage />} />

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
          <Suspense fallback={null}>
            <CookieConsent />
            <ScrollToTopButton />
          </Suspense>
          </div>
        </LanguageProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;

